import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { MagnifyingGlassIcon, ChartBarIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const Home = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isScanning, setIsScanning] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setIsScanning(true);
    
    try {
      const response = await axios.post('/api/analysis/scan', {
        url: data.url,
        sitemapUrl: data.sitemapUrl || undefined
      });

      const scanId = response.data.id;
      
      // Reset form
      reset();
      
      // Navigate to results page
      navigate(`/results/${scanId}`);
      
      toast.success('Website scan completed successfully!');
    } catch (error) {
      const message = error.response?.data?.message || 'Scan failed. Please try again.';
      
      if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please wait before scanning again.');
      } else if (error.response?.status === 402) {
        toast.error('Upgrade to Pro for unlimited scans!');
      } else {
        toast.error(message);
      }
    } finally {
      setIsScanning(false);
    }
  };

  const features = [
    {
      icon: MagnifyingGlassIcon,
      title: 'Comprehensive AEO Analysis',
      description: 'Analyze your website for Answer Engine Optimization factors including FAQ schema, structured data, and content optimization.'
    },
    {
      icon: ChartBarIcon,
      title: 'Detailed Scoring System',
      description: 'Get a 0-100 AEO score with color-coded badges and detailed breakdowns of your website\'s performance.'
    },
    {
      icon: DocumentTextIcon,
      title: 'Actionable Recommendations',
      description: 'Receive prioritized recommendations with copy-and-paste JSON-LD schema templates to improve your AEO score.'
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              SERPfinity
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              AI-Powered Search Visibility Platform - Master SEO, AEO, and AI Search Optimization
            </p>
            
            {/* Scan Form */}
            <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
                    Website URL *
                  </label>
                  <input
                    id="url"
                    type="url"
                    placeholder="https://example.com"
                    {...register('url', {
                      required: 'Website URL is required',
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'Please enter a valid URL starting with http:// or https://'
                      }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.url && (
                    <p className="mt-1 text-sm text-red-600">{errors.url.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="sitemapUrl" className="block text-sm font-medium text-gray-700 mb-2">
                    Sitemap URL (optional)
                  </label>
                  <input
                    id="sitemapUrl"
                    type="url"
                    placeholder="https://example.com/sitemap.xml"
                    {...register('sitemapUrl', {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'Please enter a valid URL starting with http:// or https://'
                      }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.sitemapUrl && (
                    <p className="mt-1 text-sm text-red-600">{errors.sitemapUrl.message}</p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={isScanning}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 flex items-center justify-center"
                >
                  {isScanning ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Scanning Website...
                    </>
                  ) : (
                    <>
                      <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                      Grade My Website
                    </>
                  )}
                </button>
                
                {!isAuthenticated && (
                  <p className="text-sm text-gray-500 text-center">
                    Free users: 1 scan per month, 10 pages max. 
                    <button 
                      onClick={() => navigate('/pricing')}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Upgrade for unlimited scans
                    </button>
                  </p>
                )}
                
                {isAuthenticated && user?.plan === 'free' && (
                  <p className="text-sm text-gray-500 text-center">
                    {user.scansThisMonth}/{user.planLimits.scansPerMonth} scans used this month. 
                    <button 
                      onClick={() => navigate('/pricing')}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Upgrade for unlimited scans
                    </button>
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Optimize for Answer Engines
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get your website ready for featured snippets, voice search, and AI-powered search results
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AEO Factors Section */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Analyze
            </h2>
            <p className="text-lg text-gray-600">
              SERPfinity analyzes 9 key factors that impact your search visibility across all platforms
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Title Tag Optimization (35-65 chars)',
              'Meta Description Presence (≤160 chars)',
              'H1 Heading Structure',
              'FAQ Sections & Schema',
              'Structured Data (JSON-LD)',
              'Author Bylines & Dates',
              'Internal Linking (≥5 links)',
              'Image Alt Text Coverage (≥70%)',
              'Table of Contents (long content)'
            ].map((factor, index) => (
              <div key={index} className="flex items-center p-4 border border-gray-200 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                <span className="text-gray-700">{factor}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to improve your AEO score?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get detailed recommendations and copy-paste schema templates
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="bg-white text-blue-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-50 transition duration-200"
          >
            View Pricing Plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;
